import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Code, Cpu, BookOpen, Mic, Wrench, Sparkles } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const projects = [
  {
    title: 'AI Story Capture System',
    category: 'AI Application',
    description: 'An experimental system for capturing personal stories through conversational AI. Uses natural language processing to guide interviews, extract key narratives, and organize memories into coherent life stories.',
    status: 'In Development',
    tech: ['Python', 'OpenAI GPT', 'Vector DB', 'React'],
    icon: Sparkles
  },
  {
    title: 'Publishing Workflow Automation',
    category: 'Publishing Technology',
    description: 'Automated pipeline for independent authors—from manuscript formatting to multi-platform distribution. Reduces publishing friction while maintaining quality.',
    status: 'Active',
    tech: ['Python', 'AWS', 'API Integrations'],
    icon: BookOpen
  },
  {
    title: 'Audio Enhancement Pipeline',
    category: 'Audio Technology',
    description: 'AI-powered audio processing for podcast and audiobook production. Automated noise reduction, leveling, and enhancement with human-quality results.',
    status: 'Prototype',
    tech: ['Python', 'ML Models', 'FFmpeg'],
    icon: Mic
  },
  {
    title: 'Entrepreneurial Assessment Tool',
    category: 'Business Tool',
    description: 'Diagnostic framework helping aspiring entrepreneurs evaluate ideas, resources, and readiness. Based on the "Use What You Got" methodology.',
    status: 'Active',
    tech: ['React', 'Node.js', 'MongoDB'],
    icon: Wrench
  },
  {
    title: 'AI Strategy Framework',
    category: 'Consulting Tool',
    description: 'Structured methodology for helping organizations assess AI opportunities, prioritize initiatives, and develop implementation roadmaps.',
    status: 'Active',
    tech: ['Framework', 'Templates', 'Workshops'],
    icon: Cpu
  },
  {
    title: 'Digital Legacy Archive',
    category: 'Platform',
    description: 'Secure platform for storing and organizing preserved life stories, documents, and media for long-term family access. Foundation for LifeCapsule AI.',
    status: 'In Development',
    tech: ['React', 'Python', 'AWS S3', 'Encryption'],
    icon: Code
  }
];

const statusColors = {
  'Active': '#10B981',
  'In Development': '#3B82F6',
  'Prototype': '#F59E0B'
};

export default function ProjectsPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl">
          <motion.span
            className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Projects
          </motion.span>
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="projects-headline"
          >
            Experiments & Explorations
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Technical experiments, prototypes, and tools developed as part of ongoing innovation work. Some become ventures, others remain useful experiments.
          </motion.p>
        </div>
      </Section>

      {/* Projects Grid */}
      <Section dark>
        <SectionTitle subtitle="Portfolio">Current Projects</SectionTitle>
        
        <motion.p
          className="text-lg text-slate-300 leading-relaxed text-center max-w-3xl mx-auto mb-8 -mt-4"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="projects-context-line"
        >
          These platforms represent the next evolution of a career spent designing systems that connect data, decisions, and human experience.
        </motion.p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <AnimatedCard key={project.title} delay={index * 0.1}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <project.icon className="w-6 h-6 text-primary" />
                </div>
                <span 
                  className="text-xs px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: `${statusColors[project.status]}20`,
                    color: statusColors[project.status]
                  }}
                >
                  {project.status}
                </span>
              </div>
              
              <span className="text-xs text-primary uppercase tracking-widest mb-2 block">
                {project.category}
              </span>
              <h3 className="text-xl font-semibold text-white mb-3">{project.title}</h3>
              <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                {project.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {project.tech.map((tech) => (
                  <span 
                    key={tech}
                    className="text-xs px-2 py-1 rounded bg-secondary text-slate-400"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </AnimatedCard>
          ))}
        </div>
      </Section>

      {/* Philosophy */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="Approach">Building Philosophy</SectionTitle>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Why Experiment?</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Innovation requires exploration. Not every project becomes a venture, but every experiment teaches something valuable. The best ideas often emerge from systematic exploration.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                These projects represent ongoing investigation into AI applications, publishing technology, and entrepreneurial tools—each one probing possibilities that might become tomorrow's solutions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">From Experiment to Venture</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                LifeCapsule AI, NewAudia, and Digital Ink AI all began as experiments. When a prototype demonstrates real value and addresses genuine need, it graduates to a full venture.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                The projects shown here are at various stages—some may become ventures, others will remain useful tools, and some will generate insights for future work.
              </p>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interested in Collaboration?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            If you're working on related challenges or see potential for collaboration on any of these projects, let's talk.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="discuss-projects-btn">
              <Link to="/contact">Discuss Projects</Link>
            </Button>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="view-ventures-btn">
              <Link to="/ventures">View Full Ventures <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
